/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Stack, Text, TooltipHost, FontIcon } from '@fluentui/react';
import { Depths } from '@uifabric/fluent-theme';
import { useId } from '@uifabric/react-hooks';
import theme from '../../../theme.style';

export default function DebugContractHeader({ data }) {
  const { contract } = data;
  const totalRocxQuantity =
    data.calcResultsBreakdown.totalQuantity + data.nrbBreakdown.totalQuantity;
  const expectedQuantity = data.expectationsBreakdown.totalQuantity;
  const deltaQuantity = totalRocxQuantity - expectedQuantity;
  const isSuccess = deltaQuantity === 0 && expectedQuantity > 0;
  const { bundles } = data.calcResultsBreakdown;

  const bundlesInfoId = useId('bundleInfo');

  return (
    <Stack horizontalAlign="start" tokens={{ childrenGap: 16 }}>
      <Text>
        <strong>Contract Terms</strong>
      </Text>

      <Stack
        style={{
          alignSelf: 'stretch',
          backgroundColor: 'white',
          boxShadow: Depths.depth4,
          padding: 14,
          borderRadius: 2,
          borderLeft: `solid 4px ${theme.palette.themePrimary}`,
        }}
        tokens={{ childrenGap: 14 }}
      >
        <Text style={{ fontSize: '1.3rem' }}>ID: #{data.contract.contractId}</Text>
        <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
          <Stack.Item>
            <Text style={{ fontWeight: 'normal', opacity: 0.75 }}>
              Silo: <strong>{contract.silo}</strong>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Text style={{ fontWeight: 'normal', opacity: 0.75 }}>
              Status: <strong>{contract.approvalStatus}</strong>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Text style={{ fontWeight: 'normal', opacity: 0.75 }}>
              Address: <strong>{contract.address}</strong>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Text style={{ fontWeight: 'normal', opacity: 0.75 }}>
              Activation Date:{' '}
              <strong>{new Date(contract.effectiveDate).toLocaleDateString()}</strong>
            </Text>
          </Stack.Item>

          <Stack.Item>
            <Text style={{ fontWeight: 'normal', opacity: 0.75 }}>
              Expiration Date:{' '}
              <strong>{new Date(contract.terminationDate).toLocaleDateString()}</strong>
            </Text>
          </Stack.Item>
        </Stack>
      </Stack>

      <Text>
        <strong>Ingestion Status</strong>
      </Text>

      {!isSuccess ? (
        <Text style={{ color: theme.palette.accentError }}>
          ✕ Not all transactions have been ingested for this month
        </Text>
      ) : (
        <Text style={{ color: theme.palette.green }}>
          ✓ All transactions have been ingested for this month
        </Text>
      )}

      <Stack
        style={{
          display: 'inline-flex',
          backgroundColor: 'white',
          boxShadow: Depths.depth4,
          padding: 14,
          borderRadius: 2,
        }}
        tokens={{
          childrenGap: 24,
        }}
        horizontal
      >
        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
          <Text style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>
            {expectedQuantity.toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </Text>
          <Text style={{ fontWeight: 'normal' }}>Staging Qty.</Text>
        </Stack>

        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
          <Text style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>
            {totalRocxQuantity.toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </Text>
          <Text style={{ fontWeight: 'normal' }}>Rocx Qty.</Text>
        </Stack>

        <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
          <Text
            style={{
              fontWeight: 'bold',
              fontSize: '1.75rem',
              color: (() => {
                if (deltaQuantity < 0) {
                  return theme.palette.accentError;
                }

                if (deltaQuantity > 0) {
                  return theme.palette.green;
                }

                return 'inherit';
              })(),
            }}
          >
            {deltaQuantity.toLocaleString('en-US', { maximumFractionDigits: 2 })}
          </Text>
          <Text style={{ fontWeight: 'normal' }}>Delta</Text>
        </Stack>
      </Stack>

      {bundles.totalQuantity > 0 && (
        <Stack
          style={{
            display: 'inline-flex',
            backgroundColor: 'white',
            boxShadow: Depths.depth4,
            padding: 14,
            borderRadius: 2,
          }}
          tokens={{
            childrenGap: 24,
          }}
          horizontal
        >
          <Stack horizontalAlign="center" tokens={{ childrenGap: 6 }}>
            <Text style={{ fontWeight: 'bold', fontSize: '1.75rem' }}>
              {bundles.totalQuantity.toLocaleString('en-US', { maximumFractionDigits: 2 })}
            </Text>

            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
              <Text style={{ fontWeight: 'normal' }}>Rocx Bundles Qty.</Text>
              <TooltipHost
                aria-label="info on rocx bundles"
                content='This value represents the total quantity found for all bundled transactions. Games can be bought in bundles which makes things a bit more complicated for reconciliation. The number for "Staging Qty." and "Rocx Qty." above does not include bundles so we show it here separately.'
                calloutProps={{ target: `#${bundlesInfoId}` }}
                styles={{
                  root: {
                    display: 'flex',
                  },
                }}
              >
                <FontIcon id={bundlesInfoId} iconName="Info" />
              </TooltipHost>
            </Stack>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

DebugContractHeader.propTypes = {
  data: PropTypes.shape({}).isRequired,
};
